import { Button } from "@mui/material";
import React from "react";
import { useAppState } from "../../../Redux/ReduxHooks";
import { CreditCardPlaceholder } from "../../Payment/PaymentEntities";
import { ApplePay } from "../../ApplePay/ApplePay";
import { GuestCreditCard } from "../../CreditCard/GuestCreditCard";
import { GooglePay } from "../../GooglePay/GooglePay";
import { PayPal } from "../../PayPal/PayPal";
import { ProceedPaymentAndUpdateBookingWithoutSmsVerificationForVoiceChannelBookings } from "../UpdatePhoneBookingWithPayment";
import { BookingDataOwnership, BookingStatus } from "../../../Services/BookingEntities";
import { Dispatch } from "../../Dispatch";
import { VoiceBookingUpdateState } from "../Redux/PhoneToCnpState";
import { getContentUrl, ContentURL } from "../../Utils/ContentURL";
import { LogEvent } from "../../../utils/LogEvent";
import { IsValidEmailFormat } from "../../Authentication/Login/CredentialHelper";
import { OptionalUI } from "../../Booking/OptionalParts/OptionalUI";
import appstore from "../../../appStore";

/**
 * Button to confirm the Fixed Price and the payment method. Upon click of this, the 3DS flow will be started if applicable or the booking will be updated with the provided payment method if payment method is valid.
 */
export const PaymentConfirmationButton: React.FC = () => {

    const bookingDetails = useAppState(state => state.PhoneToCnp.Booking)!;
    const isFixedFareSelected = useAppState(state => state.PhoneToCnp.IsFixedFareSelected);
    const isPaymentConfirmed = useAppState(state => state.PhoneToCnp.IsPaymentConfirmed);
    const paymentOption = useAppState(state => state.PhoneToCnp.PaymentOption);
    const guestPayment = useAppState(state => state.GuestPayment);
    const paymentCardError = useAppState(state => state.payment.PaymentCardError);
    const updateState = useAppState(state => state.PhoneToCnp.PaymentUpdateState);

    // Button is displayed only when payments options are displayed. i.e. Fixed Fare is selected and payment is not confirmed yet.
    if (isPaymentConfirmed) return null;
    if (!isFixedFareSelected) return null;

    // User's with read-only access (shared booking) can't view this section.
    if (bookingDetails.DataOwnership === BookingDataOwnership.ReadOnlyLink) return null;

    // Already picked up. can't convert the booking anymore. Hence, hide the button.
    if (bookingDetails.TrackingInfo.BookingStatusID >= BookingStatus.PickedUp) return null;

    let IsInactive = false;

    if (!paymentOption) IsInactive = true;

    // prevent multiple clicks while the update is in progress. The button will be re-enabled when the update is finished with an error. Button will be hidden in success case.
    if (updateState === VoiceBookingUpdateState.InProgress) IsInactive = true;

    // selected to pay with a credit card as a guest, but not all the input values are valid.
    if (paymentOption === CreditCardPlaceholder && !guestPayment.IsInlineCrediCardFormValid) IsInactive = true;

    // If there are errors with selected payment methods (e.g.: credit card expired), make the button inactive
    if (paymentCardError) IsInactive = true;

    // Email for payment receipt is optional in this flow, but if a value is entered it must be valid.
    const emailForReceiptInput = guestPayment.EmailForReceiptInput;
    if (OptionalUI.EmailForPaymentReceipt(appstore.getState()) && emailForReceiptInput && !IsValidEmailFormat(emailForReceiptInput)) IsInactive = true;

    async function ConfirmPaymentAndUpdateBooking() {

        Dispatch.PhoneToCnp.UpdateInProgress();

        LogEvent.ApplePayDebug("PaymentConfirmation", { "PaymentOption": paymentOption?.Type ?? "Unknown" });

        if (paymentOption?.Type === "ApplePay") await ApplePay.RunApplePayUiFlowAndProceedBooking();
        else if (paymentOption?.Type === "GooglePay") await GooglePay.RunGooglePayUiFlowAndProceedBooking();
        else if (paymentOption?.Type === "CreditOrDebit") await GuestCreditCard.RegisterCreditCardAndProceedBooking();
        else if (paymentOption?.Type === "PayPal") await PayPal.RunAddPayPalUiFlow();
        else ProceedPaymentAndUpdateBookingWithoutSmsVerificationForVoiceChannelBookings();
    }

    const buttonText = {
        ApplePay: "Confirm and continue to Apple Pay",
        GooglePay: "Confirm and continue to Google Pay",
        PayPal: "Confirm and continue to PayPal",
    }[paymentOption?.Type ?? ""] ?? "Confirm and continue to payment";

    return (
        <div>
            <Button variant="contained" className="payment-confirmation-button" disabled={IsInactive} onClick={ConfirmPaymentAndUpdateBooking}>{buttonText}
                {updateState === VoiceBookingUpdateState.InProgress && <img className="button-spinner" src={getContentUrl(ContentURL.images.PhoneToCnp.Loader)} />}
            </Button>
        </div>
    )
}